<script setup lang="ts">
import { TETextButton } from '@ben/vite-te'
import { ArrowRightIcon } from '@heroicons/vue/24/solid'
import { Head } from '@inertiajs/vue3'

defineProps<{
  canLogin: boolean
  canRegister: boolean
  laravelVersion: string
  phpVersion: string
  auth: {
    user: unknown
  }
}>()

const cards = ref<
  {
    href?: string
    icon: string
    title: string
    content: string
  }[]
>([
  {
    href: '/requests/smr',
    title: 'Social Media Link ändern',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-7 stroke-primary-500"><path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" /></svg>',
    content: 'Hier können für bestehende Städte die Social Media Links geändert werden.',
  },
  {
    href: '#',
    title: 'E-Mail Speicherplatz (soon)',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-7 stroke-primary-500"><path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>',
    content: 'Hier können E-Mail Speicherplatzänderungen beantragt werden.',
  },
])
</script>

<template>
  <Head title="Welcome" />

  <div
    class="bg-dots-darker dark:bg-dots-lighter relative min-h-screen bg-gray-100 bg-center selection:bg-primary-500 selection:text-white sm:flex sm:items-center sm:justify-center dark:bg-gray-900"
  >
    <div v-if="canLogin" class="z-10 p-6 text-end sm:fixed sm:end-0 sm:top-0">
      <TETextButton v-if="auth.user" tag="link" :href="route('dashboard')" method="get">
        Dashboard
      </TETextButton>

      <template v-else>
        <TETextButton tag="link" :href="route('login')"> Anmelden </TETextButton>

        <TETextButton
          v-if="canRegister"
          tag="link"
          :href="route('register')"
          class="ms-4 font-semibold text-gray-600 hover:text-gray-900 focus:rounded-sm focus:outline focus:outline-2 focus:outline-primary-500 dark:text-gray-400 dark:hover:text-white"
        >
          Register
        </TETextButton>
      </template>
    </div>

    <div class="mx-auto max-w-7xl p-6 lg:p-8">
      <div class="flex justify-center">
        <ApplicationLogo class="h-24" />
      </div>

      <div class="mt-16">
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
          <template v-for="card in cards" :key="card.href">
            <a
              :href="card.href"
              class="duration-250 flex scale-100 rounded-lg bg-white from-gray-700/50 via-transparent p-6 shadow-2xl shadow-gray-500/20 transition-all focus:outline focus:outline-2 focus:outline-primary-500 motion-safe:hover:scale-[1.01] dark:bg-gray-800/50 dark:bg-gradient-to-bl dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/5"
            >
              <div class="w-full">
                <div
                  class="flex size-16 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-800/20"
                >
                  <div v-html="card.icon" />
                </div>

                <h2 class="mt-6 text-xl font-semibold text-gray-900 dark:text-white">
                  {{ card.title }}
                </h2>

                <p class="mt-4 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                  {{ card.content }}
                </p>
              </div>

              <ArrowRightIcon class="w-10 text-primary-700" />
            </a>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.bg-dots-darker {
  background-image: url("data:image/svg+xml,%3Csvg width='30' height='30' viewBox='0 0 30 30' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.22676 0C1.91374 0 2.45351 0.539773 2.45351 1.22676C2.45351 1.91374 1.91374 2.45351 1.22676 2.45351C0.539773 2.45351 0 1.91374 0 1.22676C0 0.539773 0.539773 0 1.22676 0Z' fill='rgba(0,0,0,0.07)'/%3E%3C/svg%3E");
}

@media (prefers-color-scheme: dark) {
  .dark\:bg-dots-lighter {
    background-image: url("data:image/svg+xml,%3Csvg width='30' height='30' viewBox='0 0 30 30' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.22676 0C1.91374 0 2.45351 0.539773 2.45351 1.22676C2.45351 1.91374 1.91374 2.45351 1.22676 2.45351C0.539773 2.45351 0 1.91374 0 1.22676C0 0.539773 0.539773 0 1.22676 0Z' fill='rgba(255,255,255,0.07)'/%3E%3C/svg%3E");
  }
}
</style>
